import { LoggingService } from '@adaptiva/core/logging';
import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ResourcePool, ResourcePoolManager } from '../classes/';

const CONCURRENCY_THRESHOLD: int = 250;

/**
 * @author Jesse Rogers <jesse.rogers@adaptiva.com>
 * @summary This interceptor pools together identical, concurrent HTTP Requests to prevent redundant, concurrent REST API calls.
 * @see ResourcePoolManager
 */
export function requestPoolInterceptor(): HttpInterceptorFn {
	let _poolManager: ResourcePoolManager;

	return (_request: HttpRequest<any>, _next: HttpHandlerFn): Observable<HttpEvent<any>> => {
		_poolManager ??= new ResourcePoolManager(inject(LoggingService));

		// only cache GET requests. if any other method, just go to server.
		if (_request.method !== 'GET')
			return _next(_request);

		// use full, parameterized request URL string as ResourcePool ID,
		// as some requests go to the same URL but with different parameters
		const _url: string = _request.urlWithParams;
		// check cache for open ResourcePool with identical URL as ID
		let _requestPool: ResourcePool<HttpEvent<any>> = _poolManager.getPool(_url);

		// no elibigle request pool
		if (!_requestPool || !_requestPool.open) {
			// make a new one
			_requestPool = _poolManager.createPool({
				id: _url,
				expirable: true,
				resource: _next(_request),
				threshold: CONCURRENCY_THRESHOLD
			});
		}

		// execute request as part of new or existing RequestPool
		return _requestPool.join();
	};
}
